import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExamdojoAuthService } from '@examdojo/auth';
import { SyllabusQuery, SyllabusUserSubscriptionQuery } from '@examdojo/category';
import { UserQuery } from '@examdojo/user';
import { isNotNullish } from '@examdojo/util/nullish';
import { combineLatest, distinctUntilChanged, filter, first, map, mergeMap, of, startWith, switchMap } from 'rxjs';
import { CourseManagementService } from '../shared/course-management/course-management.service';

@Injectable()
export class OpenCourseFormDialogEffectService {
  constructor(
    private readonly courseManagementService: CourseManagementService,
    private readonly userQuery: UserQuery,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly syllabusUserSubscriptionQuery: SyllabusUserSubscriptionQuery,
    private readonly examdojoAuthService: ExamdojoAuthService,
  ) {
    combineLatest([
      this.hasCourse$.pipe(startWith(undefined)),
      this.hasSubscriptions$.pipe(startWith(undefined)),
      this.userQuery.isUserOnboarded$,
      this.examdojoAuthService.isLoggedIn$,
    ])
      .pipe(
        map(([hasCourse, hasSubscriptions, isUserOnboarded, isLoggedIn]) => {
          if (!isLoggedIn || !isUserOnboarded) {
            return false;
          }
          if (hasCourse === false) {
            return 'no_course' as const;
          }
          if (hasSubscriptions === false) {
            return 'no_subscriptions' as const;
          }
          return false;
        }),
        distinctUntilChanged(),
        mergeMap((reason) => {
          if (!reason) {
            return of(undefined);
          }
          return this.courseManagementService.openNewCourseDialog({
            canSelectExistingSubscriptions: reason === 'no_course',
            canQuit: false,
          });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private readonly hasCourse$ = this.userQuery.active$.pipe(
    filter(isNotNullish),
    map((user) => isNotNullish(user.syllabus_id)),
    distinctUntilChanged(),
  );

  private readonly hasSubscriptions$ = this.syllabusUserSubscriptionQuery.selectProp('loaded').pipe(
    first(Boolean),
    switchMap(() => this.syllabusQuery.activeProgram$.pipe(filter(isNotNullish))),
    switchMap((program) =>
      this.syllabusQuery.entities$.pipe(
        map((syllabuses) => syllabuses.filter((s) => s.program.code === program.code).map((s) => s.id)),
      ),
    ),
    switchMap((programSyllabusesIds) =>
      this.syllabusUserSubscriptionQuery.entities$.pipe(
        map((subscriptions) =>
          subscriptions.filter((subscription) => programSyllabusesIds.includes(subscription.syllabus_id)),
        ),
      ),
    ),
    map((programSyllabuses) => programSyllabuses.length > 0),
    distinctUntilChanged(),
  );
}
